import { useEffect, useState } from "react";
import { useI18n } from "@/lib/i18n";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Plus, Edit2, Trash2, Save, GripVertical, HelpCircle } from "lucide-react";

export default function AdminFaq() {
  const { t } = useI18n();
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [editLang, setEditLang] = useState("nl");
  const [showAdd, setShowAdd] = useState(false);
  const [tab, setTab] = useState("shop");
  const [newFaq, setNewFaq] = useState({ question: { nl: "", en: "", fr: "" }, answer: { nl: "", en: "", fr: "" }, category: "shop" });

  const fetchFaqs = () => {
    api.get("/admin/faqs").then((r) => setFaqs(r.data)).catch(console.error).finally(() => setLoading(false));
  };
  useEffect(() => { fetchFaqs(); }, []);

  const handleAdd = async () => {
    try {
      await api.post("/admin/faqs", newFaq);
      toast.success("FAQ toegevoegd");
      setShowAdd(false);
      setNewFaq({ question: { nl: "", en: "", fr: "" }, answer: { nl: "", en: "", fr: "" } });
      fetchFaqs();
    } catch { toast.error("Fout"); }
  };

  const handleSave = async () => {
    try {
      await api.put(`/admin/faqs/${editing.id}`, { question: editing.question, answer: editing.answer, active: editing.active, order: editing.order });
      toast.success("FAQ bijgewerkt");
      setEditing(null);
      fetchFaqs();
    } catch { toast.error("Fout"); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("FAQ verwijderen?")) return;
    try {
      await api.delete(`/admin/faqs/${id}`);
      toast.success("FAQ verwijderd");
      fetchFaqs();
    } catch { toast.error("Fout"); }
  };

  const handleToggle = async (faq) => {
    try {
      await api.put(`/admin/faqs/${faq.id}`, { active: !faq.active });
      fetchFaqs();
    } catch { toast.error("Fout"); }
  };

  if (loading) return <div className="flex items-center justify-center h-64 text-muted-foreground">{t("common.loading")}</div>;

  return (
    <div className="space-y-6" data-testid="admin-faq-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <HelpCircle className="w-6 h-6 text-blue-500" />
          <h1 className="text-2xl font-bold">FAQ Beheer</h1>
        </div>
        <Dialog open={showAdd} onOpenChange={setShowAdd}>
          <DialogTrigger asChild>
            <Button className="bg-[#2CA8AC] hover:bg-[#248F92]" data-testid="add-faq-btn"><Plus className="w-4 h-4 mr-2" />FAQ Toevoegen</Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader><DialogTitle>Nieuwe FAQ</DialogTitle></DialogHeader>
            <div className="space-y-4 py-2">
              <div>
                <Label className="font-semibold">Categorie</Label>
                <div className="flex gap-2 mt-2">
                  <button onClick={() => setNewFaq({...newFaq, category: "shop"})} className={`px-3 py-1.5 rounded-lg text-sm ${newFaq.category === "shop" ? "bg-[#2CA8AC] text-white" : "bg-accent/50"}`}>Webshop</button>
                  <button onClick={() => setNewFaq({...newFaq, category: "affiliate"})} className={`px-3 py-1.5 rounded-lg text-sm ${newFaq.category === "affiliate" ? "bg-emerald-600 text-white" : "bg-accent/50"}`}>Affiliate</button>
                </div>
              </div>
              <div><Label className="font-semibold">Vraag (meertalig)</Label>
                <Tabs value={editLang} onValueChange={setEditLang} className="mt-2">
                  <TabsList className="mb-2"><TabsTrigger value="nl">NL</TabsTrigger><TabsTrigger value="en">EN</TabsTrigger><TabsTrigger value="fr">FR</TabsTrigger></TabsList>
                  {["nl","en","fr"].map(lang => <TabsContent key={lang} value={lang}><Input value={newFaq.question[lang] || ""} onChange={e => setNewFaq({...newFaq, question: {...newFaq.question, [lang]: e.target.value}})} placeholder={`Vraag (${lang.toUpperCase()})`} /></TabsContent>)}
                </Tabs>
              </div>
              <div><Label className="font-semibold">Antwoord (meertalig, HTML)</Label>
                <Tabs value={editLang} onValueChange={setEditLang} className="mt-2">
                  <TabsList className="mb-2"><TabsTrigger value="nl">NL</TabsTrigger><TabsTrigger value="en">EN</TabsTrigger><TabsTrigger value="fr">FR</TabsTrigger></TabsList>
                  {["nl","en","fr"].map(lang => <TabsContent key={lang} value={lang}><Textarea rows={8} value={newFaq.answer[lang] || ""} onChange={e => setNewFaq({...newFaq, answer: {...newFaq.answer, [lang]: e.target.value}})} className="font-mono-data text-sm" placeholder={`HTML (${lang.toUpperCase()})`} /></TabsContent>)}
                </Tabs>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAdd(false)}>Annuleren</Button>
              <Button onClick={handleAdd} className="bg-blue-600" data-testid="save-new-faq-btn">Toevoegen</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Category tabs */}
      <div className="flex gap-2">
        <button onClick={() => setTab("shop")} className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${tab === "shop" ? "bg-[#2CA8AC] text-white" : "bg-accent/50 text-muted-foreground"}`}>Webshop FAQ's ({faqs.filter(f => !f.category || f.category === "shop").length})</button>
        <button onClick={() => setTab("affiliate")} className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${tab === "affiliate" ? "bg-emerald-600 text-white" : "bg-accent/50 text-muted-foreground"}`}>Affiliate FAQ's ({faqs.filter(f => f.category === "affiliate").length})</button>
      </div>

      <div className="space-y-3">
        {faqs.filter(f => tab === "shop" ? (!f.category || f.category === "shop") : f.category === "affiliate").map((faq, i) => (
          <Card key={faq.id} className={`bg-card border-border ${!faq.active ? "opacity-50" : ""}`} data-testid={`admin-faq-${faq.id}`}>
            <CardContent className="p-4 flex items-start gap-4">
              <div className="text-muted-foreground mt-1 text-sm font-mono-data w-6 text-center">{i + 1}</div>
              <div className="flex-1 min-w-0">
                <p className="font-medium">{faq.question?.nl || "-"}</p>
                <p className="text-xs text-muted-foreground mt-1 truncate">{(faq.answer?.nl || "").replace(/<[^>]*>/g, "").slice(0, 100)}...</p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Badge variant="outline" className="text-xs">NL/EN/FR</Badge>
                <Switch checked={faq.active} onCheckedChange={() => handleToggle(faq)} data-testid={`toggle-faq-${faq.id}`} />
                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => { setEditing({ ...faq }); setEditLang("nl"); }} data-testid={`edit-faq-${faq.id}`}>
                  <Edit2 className="w-3.5 h-3.5" />
                </Button>
                <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => handleDelete(faq.id)} data-testid={`delete-faq-${faq.id}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {editing && (
        <Dialog open={!!editing} onOpenChange={() => setEditing(null)}>
          <DialogContent className="max-w-2xl">
            <DialogHeader><DialogTitle>FAQ Bewerken</DialogTitle></DialogHeader>
            <div className="space-y-4 py-2">
              <div>
                <Label className="font-semibold">Vraag</Label>
                <Tabs value={editLang} onValueChange={setEditLang} className="mt-2">
                  <TabsList className="mb-2"><TabsTrigger value="nl">NL</TabsTrigger><TabsTrigger value="en">EN</TabsTrigger><TabsTrigger value="fr">FR</TabsTrigger></TabsList>
                  {["nl","en","fr"].map(lang => <TabsContent key={lang} value={lang}><Input value={editing.question?.[lang] || ""} onChange={e => setEditing({...editing, question: {...editing.question, [lang]: e.target.value}})} placeholder={`Vraag (${lang.toUpperCase()})`} /></TabsContent>)}
                </Tabs>
              </div>
              <div>
                <Label className="font-semibold">Antwoord (HTML)</Label>
                <Tabs value={editLang} onValueChange={setEditLang} className="mt-2">
                  <TabsList className="mb-2"><TabsTrigger value="nl">NL</TabsTrigger><TabsTrigger value="en">EN</TabsTrigger><TabsTrigger value="fr">FR</TabsTrigger></TabsList>
                  {["nl","en","fr"].map(lang => <TabsContent key={lang} value={lang}><Textarea rows={8} value={editing.answer?.[lang] || ""} onChange={e => setEditing({...editing, answer: {...editing.answer, [lang]: e.target.value}})} className="font-mono-data text-sm" placeholder={`HTML (${lang.toUpperCase()})`} /></TabsContent>)}
                </Tabs>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditing(null)}>Annuleren</Button>
              <Button onClick={handleSave} className="bg-blue-600" data-testid="save-edit-faq-btn"><Save className="w-4 h-4 mr-2" />Opslaan</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
